import { clamp, debounce, isEqual } from "lodash-es"
import { computed, observable, runInAction } from "mobx"

import { RE_FILE_MENTION_UNESCAPED_PATH, RE_PROFILE_MENTION_USERNAME } from "@/components/Markdown/mentions.ts"
import { parsePartiallyCompletedEntity } from "@/components/PromptInput/Completion/completionParser.ts"
import { Lifetime } from "@/lib/Lifetime.ts"
import { getLogger } from "@/lib/logging.ts"

import type { CompletionProvider } from "@/components/PromptInput/Completion/completionProviders.ts"
import type { TD_MemberProfile_Minimal } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Lifetimed } from "@/lib/Lifetime.ts"
import type { IComputedValue, IObservableValue } from "mobx"


export type BaseCompletionDropdownItem = {
  type: string
  entityText: string
  completedText: string
}

export type CompletionDropdownItem = ProfileMentionDropdownItem
  | FileMentionDropdownItem
  | PartialMentionDropdownItem
  | FixedMentionDropdownItem<"agent">

export type CompletionItemType = CompletionDropdownItem["type"]

export interface ProfileMentionDropdownItem extends BaseCompletionDropdownItem {
  type: "profile"
  profile: TD_MemberProfile_Minimal
}

export interface FileMentionDropdownItem extends BaseCompletionDropdownItem {
  type: "file"
  path: string
}

export interface FixedMentionDropdownItem<T extends string> extends BaseCompletionDropdownItem {
  type: "fixed"
  entityText: T
  completedText: `@${T}`
  description: string
}

export interface PartialMentionDropdownItem extends BaseCompletionDropdownItem {
  type: "partial"
  description: string
}

export type CompletionResult = {
  text: string
  caretOffset: number
}

type CompletedEntityAtCaret = CompletedEntity & {
  caretOffset: number
}

export interface CompletionModel extends Lifetimed {
  isVisible: IComputedValue<boolean>
  isLoading: IObservableValue<boolean>
  items: IObservableValue<CompletionDropdownItem[]>
  hasMore: IObservableValue<boolean>
  selectionIndex: IObservableValue<number>
  anchorOffset: IObservableValue<number>
  query: IObservableValue<string>

  loadMore(): void

  close(): void

  onChange(text: string, caretOffset: number): void

  onInputFocus(): void

  onInputBlur(): void

  down(): void

  up(): void

  select(index: number): void

  complete(item: CompletionDropdownItem): void
}

export class CompletionModelImpl implements CompletionModel, Lifetimed {
  private readonly focused = observable.box(false)
  private readonly suggestions: IObservableValue<CompletionSuggestions | null> = observable.box(null)
  readonly items: IObservableValue<CompletionDropdownItem[]> = observable.box([])

  constructor(
    readonly lifetime: Lifetime,
    readonly completionProvider: CompletionProvider,
    readonly getText: () => string,
    private readonly onCompleted: (result: CompletionResult) => void,
    readonly batchSize?: number
  ) {
  }

  readonly isVisible = computed(() => {
    const suggestions = this.suggestions.get()
    if (suggestions === null) {
      return false
    }
    // if (!this.focused.get()) {
    //   return false
    // }
    const items = this.items.get()
    if (items.length === 0) {
      return false
    }
    if (items.length > 1) {
      return true
    }
    const item = items[0]
    return !(suggestions.entity.type === item.type && suggestions.entity.completedText.startsWith(item.completedText))
  })

  readonly query = computed(() => {
    if (!this.isVisible.get()) {
      return ""
    }
    const suggestions = this.suggestions.get()
    if (suggestions === null) {
      return ""
    }
    return suggestions.entity.entityText
  })

  readonly selectionIndex = computed(() => {
    if (this.isVisible.get()) {
      const suggestions = this.suggestions.get()
      if (suggestions) {
        return clamp(suggestions.selectionIndex.get(), 0, this.items.get().length - 1)
      }
    }
    return -1
  })

  readonly isLoading = computed(() => {
    return this.isVisible.get() && (this.suggestions.get()?.isLoading.get() === true)
  })

  readonly hasMore = computed(() => {
    return this.isVisible.get() && (this.suggestions.get()?.hasMore.get() === true)
  })

  readonly anchorOffset = computed(() => {
    return this.isVisible.get() ? this.suggestions.get()?.entity.completedTextRange[0] || 0 : 0
  })

  readonly loadMore = () => {
    const suggestions = this.suggestions.get()
    if (suggestions) {
      void suggestions.loadMore()
    }
  }

  readonly onInputFocus = () => {
    runInAction(() => {
      this.focused.set(true)
    })
  }

  readonly onInputBlur = () => {
    runInAction(() => {
      this.focused.set(false)
    })
  }

  // Handle input changes
  readonly onChange = debounce((text: string, caretOffset: number) => {
    const atSignIndex = text.substring(0, clamp(caretOffset, 0, text.length)).lastIndexOf("@")
    if (atSignIndex !== -1 && (atSignIndex === 0 || /\s/.test(text[atSignIndex - 1]))) {
      const newEntityWithoutCaret = parsePartiallyCompletedEntity(text, atSignIndex)
      let newEntity: CompletedEntityAtCaret | null = null
      if (newEntityWithoutCaret && caretOffset <= newEntityWithoutCaret.completedTextRange[1]) {
        newEntity = {
          caretOffset: caretOffset,
          ...newEntityWithoutCaret,
          entityText: text.substring(newEntityWithoutCaret.entityTextRange[0], Math.min(newEntityWithoutCaret.entityTextRange[1], caretOffset)),
          entityTextRange: [newEntityWithoutCaret.entityTextRange[0], Math.min(newEntityWithoutCaret.entityTextRange[1], caretOffset)]
        }
      }
      if (!CompletionSuggestions.forSameEntity(this.suggestions.get(), newEntity)) {
        this.suggestions.get()?.lifetime.terminate()
        if (newEntity) {
          runInAction(() => {
            const oldEntity = this.suggestions.get()?.entity || null
            this.suggestions.set(new CompletionSuggestions(new Lifetime(this.lifetime, "CompletionModel:suggestions"), newEntity, this.completionProvider, this.onResultsLoaded, this.batchSize))
            this.onCompletedEntityChanged(oldEntity, newEntity)
          })
        } else {
          this.close()
        }
      }
    } else {
      this.close()
    }
  }, 150, { leading: true, maxWait: 1000 })

  readonly close = () => {
    runInAction(() => {
      this.suggestions.get()?.lifetime?.terminate()
      this.suggestions.set(null)
      this.items.set([])
    })
  }

  readonly onCompletedEntityChanged = (oldEntity: CompletedEntityAtCaret | null, newEntity: CompletedEntityAtCaret) => {
    if (!oldEntity) return
    if (oldEntity.type !== newEntity.type) {
      this.items.set([])
    }
    if (oldEntity.entityText === newEntity.entityText) {
      return
    }
    if (newEntity.entityText.startsWith(oldEntity.entityText)) {
      return
      // this.items.set(this.items.get().filter(item => item.value.startsWith(newLocation.value)))
    } else if (oldEntity.entityText.startsWith(newEntity.entityText)) {
      return
    } else {
      this.items.set([])
    }
  }

  readonly onResultsLoaded = (results: CompletionDropdownItem[]) => {
    this.items.set(results)
  }

  readonly down = () => {
    this.shift(1)
  }

  readonly up = () => {
    this.shift(-1)
  }

  readonly select = (index: number) => {
    if (!this.isVisible.get()) return
    const items = this.items.get()
    const suggestions = this.suggestions.get()
    if (items.length === 0 || !suggestions) {
      return
    }
    runInAction(() => {
      suggestions.selectionIndex.set(clamp(index, 0, items.length - 1))
    })
  }

  readonly shift = (direction: -1 | 1) => {
    this.select(this.selectionIndex.get() + direction)
  }

  readonly complete = (item: CompletionDropdownItem) => {
    const text = this.getText()
    const suggestions = this.suggestions.get()
    if (!suggestions) {
      return
    }
    let replaceRange: [number, number]
    if (suggestions.entity.completedText === item.completedText) {
      replaceRange = suggestions.entity.completedTextRange
    } else {
      const [start, end] = suggestions.entity.completedTextRange
      replaceRange = [start, Math.min(end, suggestions.entity.caretOffset + 1)]
    }
    const [start, end] = replaceRange
    if (start >= text.length || end > text.length) {
      return
    }
    const prefix = text.substring(0, start)
    let suffix = text.substring(end)
    if (item.type !== "partial" && (suffix.length == 0 || suffix[0].match(RE_FILE_MENTION_UNESCAPED_PATH) || suffix[0].match(RE_PROFILE_MENTION_USERNAME))) {
      suffix = ` ${suffix}`
    }
    const resultText = `${prefix}${item.completedText}${suffix}`
    const caretOffset = Math.min(start + item.completedText.length + 1, resultText.length)
    this.onCompleted({
      text: resultText,
      caretOffset: caretOffset
    })
  }
}

const log = getLogger("CompletionSuggestions")

class CompletionSuggestions implements Lifetimed {
  readonly hasMore = observable.box(true)
  readonly isLoading = observable.box(false)
  readonly selectionIndex = observable.box(0)
  private batchOffset: number | null = null
  private readonly items: CompletionDropdownItem[] = []

  constructor(
    readonly lifetime: Lifetime,
    readonly entity: CompletedEntityAtCaret,
    private readonly completionProvider: CompletionProvider,
    private readonly onResultsLoaded: (results: CompletionDropdownItem[]) => void,
    private readonly batchSize?: number,
  ) {
    void this.loadMore()
  }

  static forSameEntity(suggestions: CompletionSuggestions | null, otherEntity: CompletedEntityAtCaret | null): boolean {
    return isEqual(suggestions?.entity, otherEntity)
  }

  async loadMore() {
    if (this.isLoading.get() || !this.hasMore.get()) return
    try {
      runInAction(() => {
        this.isLoading.set(true)
      })
      const result = await this.completionProvider({
        batchInfo: {
          batchSize: this.batchSize ?? 5,
          offset: this.batchOffset ? `${this.batchOffset}` : null
        },
        type: this.entity.type,
        value: this.entity.entityText
      })
      for (const item of result) {
        let exists = false
        for (const existingItem of this.items) {
          if (isEqual(item, existingItem)) {
            exists = true
            break
          }
        }
        if (!exists) {
          this.items.push(item)
        }
      }
      log.debug("Query:", this.entity.entityText, "+", this.entity.type, "->", result.length, "results")
      runInAction(() => {
        this.hasMore.set(result.length >= (this.batchSize ?? 5))
        if (!this.lifetime.terminated) {
          this.onResultsLoaded(this.items)
        }
      })
    } finally {
      runInAction(() => {
        this.isLoading.set(false)
      })
    }
  }
}

export type CompletedEntity = {
  completedText: string
  completedTextRange: [number, number]
  type: "profile" | "file"
  entityText: string
  entityTextRange: [number, number]
}
